import { useEffect, useRef, useState } from "react";
import { abwicklungAuswerten, abwicklungBeginnen, abwicklungSchritt, aufnahmeStarten, kapselErkennen, vorschauSchritt } from "../kern/abwicklung.js";
import { fotoserieAuswerten } from "../kern/fotoserie.js";
import { exifBrennweite } from "../kern/exif.js";
import { umrissLinie } from "../kern/flaschenlage.js";
import { FLASCHENFORMEN, FORM_REIHE, KAPSEL_ARTEN, formVon, kapselArt, vorschlagForm } from "../kern/flasche.js";
import { aufKanteZeichnen, bildLaden, rundumHochladen } from "../dienste/fotos.js";
import { FlaschenLupe, FlaschenModell } from "../ui/flasche.jsx";
import { usePersistentState } from "../ui/hooks.jsx";
import { Btn } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";


/* ═══════════════════════════════════════════════════════════════
   Rundum-Etikett aufnehmen

   Ablauf: Kamera an → Flasche in den Umriss stellen; sobald sie
   erkannt ist, legt sich die Linie um die echte Flasche → „Start“ →
   Flasche einmal ganz herumdrehen, gern in Schüben (umgreifen ist
   erlaubt) → die Aufnahme endet von selbst nach gut einer Umdrehung
   → „Wird berechnet …“ → Vorschau als drehbare Flasche, Kapsel
   prüfen → Speichern.

   Warum ein eigenes Vollbild statt des gemeinsamen Modal-Rahmens:
   Das Kamerabild braucht die ganze Fläche, und der Umriss muss genau
   über dem Bild liegen. Der Zustand „welcher Dialog ist offen“ bleibt
   trotzdem der eine in shell.jsx (Typ „rundum“); nur das Aussehen
   ist anders.

   Zwei Bildquellen je Kamerabild:
   - **Übersicht**: das Kamerabild verkleinert auf 960 Zeilen (bei
     Querformat mittig beschnitten). Darauf laufen Erkennung,
     Verfolgung und Messband — die Rechenzeit hängt an dieser Größe.
   - **Vollbild**: Für den schmalen Streifen um die Flaschenmitte
     wird nur dessen Rechteck in voller Kameraauflösung gelesen
     (`drawImage` des Ausschnitts, dann `getImageData`). Daraus
     entsteht das Ergebnis; es ist so scharf, wie die Kamera liefert.

   Der Umriss liegt in einem SVG mit dem Koordinatensystem des
   Kamerabilds und `preserveAspectRatio="xMidYMid slice"` — dieselbe
   Abbildung wie `object-fit: cover` beim Video. Gestrichelt: die
   Vorgabe der gewählten Form. Durchgezogen: die erkannte Flasche,
   bei jedem Bild nachgeführt (direkt am SVG-Pfad, ohne React — ein
   Neuzeichnen je Kamerabild kostete auf dem Telefon mehr als die
   Messung).

   Zweite Aufnahmeart: **Fotos** (Umschalter „Video | Fotos“, die Wahl
   bleibt für die Sitzung). Statt der Kamera eine Liste: Fotos
   aufnehmen (je eines über die Kamera des Telefons, in voller
   Auflösung) oder aus der Mediathek wählen; ab drei Fotos „Berechnen“.
   Die Fotos werden auf 2400 Pixel lange Kante verkleinert, die
   Brennweite kommt aus den EXIF-Daten. Danach dieselbe Prüfen-Ansicht.
   Nach einem Fehler bleiben die Fotos stehen — eines tauschen oder
   ergänzen genügt. Die Fotos lassen sich durch Ziehen umsortieren
   (Tastatur: Pfeile); Foto 1 ist die Vorderseite des Modells.

   Die Bildverarbeitung selbst steht in kern/flaschenlage.js,
   kern/abwicklung.js und kern/fotoserie.js; diese Datei liest nur
   Bilder und zeigt an.
   ═══════════════════════════════════════════════════════════════ */

const RAHMEN_ANTEIL = 0.84;        /* Flaschenhöhe im Kamerabild */
const UEBERSICHT_HOEHE = 960;      /* Zeilen des Übersichtsbilds */

const KAPSEL_LABEL = { folie: "Folie", wachs: "Wachs", schraub: "Schraubverschluss", ohne: "Ohne Kapsel" };

const FOTO_KANTE = 2400;           /* Fotos werden auf diese lange Kante verkleinert */
const FOTOS_MAX = 10;

function fotoFehlertext(erg) {
  if (erg.fehler === "zuWenige") return t("Bitte mindestens drei Fotos wählen, besser sechs rundum.");
  if (erg.fehler === "flascheFehlt") return t("Auf Foto {n} wurde keine Flasche erkannt. Die ganze Flasche sollte aufrecht im Bild stehen.", { n: erg.foto + 1 });
  if (erg.fehler === "keineUeberlappung") return t("Foto {n} passt zu keinem anderen Foto. Zwischen zwei Fotos die Flasche nur um etwa ein Sechstel weiterdrehen.", { n: erg.foto + 1 });
  if (erg.fehler === "luecke") return t("Die Fotos zeigen die Flasche nicht von allen Seiten. Bitte rundum fotografieren, etwa alle 60°.");
  return t("Die Berechnung ist fehlgeschlagen.");
}

/* Ein gewähltes Foto für die Auswertung: verkleinert (EXIF-Drehung
   beachtet) und mit der Brennweite aus den EXIF-Daten. */
async function fotoLesen(datei) {
  let exif = null;
  try { exif = exifBrennweite(await datei.slice(0, 256 * 1024).arrayBuffer()); } catch (e) { /* ohne */ }
  const bild = await bildLaden(datei);
  const { leinwand, breite, hoehe } = aufKanteZeichnen(bild, FOTO_KANTE);
  if (bild.close) bild.close();
  const daten = leinwand.getContext("2d").getImageData(0, 0, breite, hoehe);
  leinwand.width = 0; leinwand.height = 0;
  return { data: daten.data, width: breite, height: hoehe, exif };
}


/* Umriss der Form als SVG-Pfad in Videokoordinaten. */
function umrissPfad(form, cx, oben, hoehe) {
  const s = hoehe / form.hoehe;
  const rechts = form.profil.map(([h, r]) => [cx + r * s, oben + (form.hoehe - h) * s]);
  const links = [...form.profil].reverse().map(([h, r]) => [cx - r * s, oben + (form.hoehe - h) * s]);
  return "M" + [...rechts, ...links].map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L") + " Z";
}


function kameraFehlertext(e) {
  const name = e && e.name;
  if (name === "NotAllowedError" || name === "SecurityError") return t("Kein Zugriff auf die Kamera. Bitte den Zugriff in den Einstellungen des Browsers erlauben.");
  if (name === "NotFoundError" || name === "OverconstrainedError") return t("Es wurde keine Kamera gefunden.");
  return t("Die Kamera konnte nicht gestartet werden.");
}


/* Übersicht und Vollbild für ein Video der Größe b × h: Ausschnitt
   (höchstens drei Viertel so breit wie hoch, mittig), Maßstab und die
   Leinwände zum Lesen. */
function bildquellen(video, b, h) {
  const faktor = Math.min(1, UEBERSICHT_HOEHE / h);
  const qb = Math.min(b, Math.round(0.75 * h));
  const quelle = { x: Math.round((b - qb) / 2), y: 0, b: qb, h };
  const ueb = document.createElement("canvas");
  ueb.width = Math.round(qb * faktor); ueb.height = Math.round(h * faktor);
  const uebCtx = ueb.getContext("2d", { willReadFrequently: true });
  const streifen = document.createElement("canvas");
  const streifenCtx = streifen.getContext("2d", { willReadFrequently: true });
  let letzte = null;
  return {
    faktor, quelle,
    uebersicht() {
      uebCtx.drawImage(video, quelle.x, quelle.y, quelle.b, quelle.h, 0, 0, ueb.width, ueb.height);
      letzte = uebCtx.getImageData(0, 0, ueb.width, ueb.height);
      return letzte;
    },
    /* Vollbild: Koordinaten relativ zum Ausschnitt, in Kamerapixeln */
    voll: {
      breite: quelle.b, hoehe: quelle.h,
      lies(x, y, bw, bh) {
        /* Kleine Kamera: Die Übersicht ist schon das Vollbild — aus ihr
           kopieren, statt das Video ein zweites Mal zu lesen */
        if (faktor === 1 && letzte && letzte.width === quelle.b) {
          const aus = new ImageData(bw, bh);
          const x0 = Math.max(0, x), x1 = Math.min(letzte.width, x + bw);
          for (let r = 0; r < bh; r++) {
            const yy = y + r;
            if (yy < 0 || yy >= letzte.height || x1 <= x0) continue;
            aus.data.set(letzte.data.subarray((yy * letzte.width + x0) * 4, (yy * letzte.width + x1) * 4), (r * bw + x0 - x) * 4);
          }
          return aus;
        }
        if (streifen.width < bw || streifen.height < bh) {
          streifen.width = Math.max(streifen.width, bw); streifen.height = Math.max(streifen.height, bh);
        }
        streifenCtx.drawImage(video, quelle.x + x, quelle.y + y, bw, bh, 0, 0, bw, bh);
        return streifenCtx.getImageData(0, 0, bw, bh);
      },
    },
    /* Punkt im Übersichtsbild → Videokoordinaten */
    zuVideo: ([x, y]) => [quelle.x + x / faktor, quelle.y + y / faktor],
  };
}


/* Gewählte Fotos als Raster zum Umsortieren: Ziehen (Finger oder Maus)
   schiebt ein Foto an eine andere Stelle, die übrigen rücken nach.
   Die Plätze werden beim Greifen vermessen; das gezogene Foto folgt dem
   Finger relativ zu seinem jeweiligen Platz. Bewegung und Loslassen
   hören am Fenster mit (React verschiebt beim Umsortieren Knoten im
   DOM, dabei ginge eine Zeigerbindung am Foto verloren). Ein Tippen
   ohne Bewegung tut nichts. Mit der Tastatur verschieben Pfeil links
   oder rechts (auch hoch, runter) um eine Stelle, Pos1 an die erste.
   Foto 1 ist vorn. */
function FotoListe({ fotos, aktiv, onReihe, onWeg }) {
  const listeRef = useRef(null);
  const zugRef = useRef(null);
  const fotosRef = useRef(fotos);
  fotosRef.current = fotos;
  const [zug, setZug] = useState(null);           /* { id, dx, dy } während des Ziehens */
  const [fokus, setFokus] = useState(null);       /* nach dem Verschieben per Tastatur */
  const [ansage, setAnsage] = useState("");

  const verschieben = (id, ziel) => {
    const alt = fotosRef.current, von = alt.findIndex((f) => f.id === id);
    ziel = Math.max(0, Math.min(alt.length - 1, ziel));
    if (von < 0 || von === ziel) return von;
    const neu = [...alt];
    const [f] = neu.splice(von, 1);
    neu.splice(ziel, 0, f);
    fotosRef.current = neu;
    onReihe(neu);
    return ziel;
  };

  useEffect(() => {
    if (!fokus || !listeRef.current) return;
    const el = listeRef.current.querySelector(`[data-foto="${fokus}"]`);
    if (el && document.activeElement !== el) el.focus();
    setFokus(null);
  }, [fokus, fotos]);

  const greifen = (e, id) => {
    if (!aktiv || zugRef.current || (e.pointerType === "mouse" && e.button !== 0) || e.target.closest("button")) return;
    const plaetze = [...listeRef.current.children].map((el) => el.getBoundingClientRect());
    const i = fotosRef.current.findIndex((f) => f.id === id);
    window.addEventListener("pointermove", ziehen);
    window.addEventListener("pointerup", loslassen);
    window.addEventListener("pointercancel", loslassen);
    const weg = () => {
      window.removeEventListener("pointermove", ziehen);
      window.removeEventListener("pointerup", loslassen);
      window.removeEventListener("pointercancel", loslassen);
    };
    zugRef.current = { id, pointerId: e.pointerId, start: [e.clientX, e.clientY], griff: [e.clientX - plaetze[i].left, e.clientY - plaetze[i].top], plaetze, laeuft: false, weg };
  };
  /* beim Schließen mitten im Ziehen */
  useEffect(() => () => { if (zugRef.current) zugRef.current.weg(); }, []);
  function ziehen(e) {
    const z = zugRef.current;
    if (!z || e.pointerId !== z.pointerId) return;
    const x = e.clientX, y = e.clientY;
    if (!z.laeuft) {
      if (Math.hypot(x - z.start[0], y - z.start[1]) < 6) return;
      z.laeuft = true;
    }
    e.preventDefault();
    /* Platz unter dem Finger (sonst der nächste) */
    let ziel = z.plaetze.findIndex((r) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom);
    if (ziel < 0) {
      let bd = Infinity;
      z.plaetze.forEach((r, k) => { const d = Math.hypot(x - (r.left + r.right) / 2, y - (r.top + r.bottom) / 2); if (d < bd) { bd = d; ziel = k; } });
    }
    const jetzt = verschieben(z.id, ziel);
    const platz = z.plaetze[jetzt];
    setZug({ id: z.id, dx: x - z.griff[0] - platz.left, dy: y - z.griff[1] - platz.top });
  }
  function loslassen(e) {
    const z = zugRef.current;
    if (!z || e.pointerId !== z.pointerId) return;
    zugRef.current = null;
    z.weg();
    setZug(null);
    if (z.laeuft) {
      const i = fotosRef.current.findIndex((f) => f.id === z.id);
      setAnsage(t("Foto jetzt an Stelle {n}.", { n: i + 1 }));
    }
  }
  const taste = (e, id, i) => {
    if (!aktiv) return;
    const ziel = { ArrowLeft: i - 1, ArrowUp: i - 1, ArrowRight: i + 1, ArrowDown: i + 1, Home: 0 }[e.key];
    if (ziel == null) return;
    e.preventDefault();
    const neu = verschieben(id, ziel);
    setFokus(id);
    setAnsage(t("Foto jetzt an Stelle {n}.", { n: neu + 1 }));
  };

  return (
    <>
      <ul ref={listeRef} className="wl-rundumfotoliste">
        {fotos.map((f, i) => {
          const gezogen = zug && zug.id === f.id;
          return (
            <li key={f.id} data-foto={f.id} className={"wl-rundumfoto" + (aktiv ? " ist-beweglich" : "") + (gezogen ? " ist-gezogen" : "")}
              style={gezogen ? { transform: `translate(${zug.dx}px, ${zug.dy}px)` } : undefined}
              tabIndex={aktiv ? 0 : undefined}
              aria-label={(i === 0 ? t("Foto {n}, Vorderseite", { n: i + 1 }) : t("Foto {n}", { n: i + 1 })) + (aktiv ? ". " + t("Zum Verschieben ziehen oder Pfeiltasten.") : "")}
              onPointerDown={(e) => greifen(e, f.id)}
              onKeyDown={(e) => taste(e, f.id, i)}>
              <img src={f.url} alt="" draggable={false} />
              <span className="wl-rundumfotonr" aria-hidden="true">{i === 0 ? `1 · ${t("vorne")}` : i + 1}</span>
              {aktiv && (
                <button type="button" className="wl-rundumfotoweg" onClick={() => onWeg(f.id)}
                  aria-label={t("Foto {n} entfernen", { n: i + 1 })}>×</button>
              )}
            </li>
          );
        })}
      </ul>
      <span className="wl-nurvorlesen" aria-live="polite">{ansage}</span>
    </>
  );
}


function RundumAufnahme({ wein, jg, onSave, onCancel }) {
  const videoRef = useRef(null);
  const stromRef = useRef(null);
  const laufRef = useRef(null);
  const umrissRef = useRef(null);
  const [phase, setPhase] = useState("kamera");     /* kamera | laeuft | rechnet | pruefen | fehler */
  const [form, setForm] = useState(() => (jg.rundum && jg.rundum.form) || vorschlagForm(wein));
  const [video, setVideo] = useState(null);         /* { b, h } sobald bekannt */
  const [kameraFehler, setKameraFehler] = useState(null);
  const [erkannt, setErkannt] = useState(false);
  const [stand, setStand] = useState(null);
  const [rechnet, setRechnet] = useState(0);
  const [laufFehler, setLaufFehler] = useState(null);
  const [ergebnis, setErgebnis] = useState(null);   /* { leinwand, flachUrl, form, profil, vorne, info } */
  const [kapsel, setKapsel] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const [speicherFehler, setSpeicherFehler] = useState(null);
  const [lupe, setLupe] = useState(false);          /* große Ansicht des Ergebnisses */
  /* Aufnahmeart: Video (Kamera, einmal herumdrehen) oder Fotos */
  const [quelle, setQuelle] = usePersistentState("rundum.quelle", "video");
  const [fotos, setFotos] = useState([]);          /* [{ id, datei, url }] */
  const aufnahmeRef = useRef(null);
  const auswahlRef = useRef(null);
  const fotosRef = useRef(fotos);
  fotosRef.current = fotos;
  useEffect(() => () => { for (const f of fotosRef.current) URL.revokeObjectURL(f.url); }, []);

  /* ── Kamera ── */
  const kameraStoppen = () => {
    const strom = stromRef.current;
    stromRef.current = null;
    if (strom) for (const spur of strom.getTracks()) { try { spur.stop(); } catch (e) { /* schon aus */ } }
  };

  const kameraStarten = async () => {
    setKameraFehler(null);
    setVideo(null);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setKameraFehler(t("Die Kamera ist in diesem Browser nicht verfügbar."));
      return;
    }
    try {
      const strom = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      stromRef.current = strom;
      const el = videoRef.current;
      if (!el) { kameraStoppen(); return; }
      el.srcObject = strom;
      await el.play().catch(() => {});
    } catch (e) {
      setKameraFehler(kameraFehlertext(e));
    }
  };

  /* Kamera an, sobald das Kamerabild zu sehen ist — beim Öffnen und
     nach „Wiederholen“ (dann gibt es das Videoelement erst nach dem
     Umschalten der Ansicht). */
  useEffect(() => {
    if (phase === "kamera" && quelle === "video" && !stromRef.current) kameraStarten();
    if (quelle !== "video" && stromRef.current) { laufRef.current = null; kameraStoppen(); setVideo(null); }
  }, [phase, quelle]);
  useEffect(() => () => { laufRef.current = null; kameraStoppen(); }, []);

  /* Escape bricht ab — wie bei jedem anderen Dialog. */
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !speichert && !lupe) abbrechen(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const videoBereit = () => {
    const el = videoRef.current;
    if (el && el.videoWidth) setVideo((v) => (v && v.b === el.videoWidth && v.h === el.videoHeight ? v : { b: el.videoWidth, h: el.videoHeight }));
  };

  /* Vorgabe: wo die Flasche laut eingeblendetem Umriss steht. */
  const rahmen = video ? (() => {
    const hoehe = video.h * RAHMEN_ANTEIL;
    return { cx: video.b / 2, oben: (video.h - hoehe) / 2, hoehe };
  })() : null;

  /* ── Bildschleife ──
     Ein Lauf ist ein Objekt in laufRef; wird es ersetzt oder geleert,
     endet seine Schleife beim nächsten Bild von selbst. */
  const schleife = (lauf) => {
    const el = videoRef.current;
    if (!el) return;
    const rvfc = typeof el.requestVideoFrameCallback === "function";
    const weiter = () => {
      if (laufRef.current !== lauf || lauf.beendet) return;
      if (rvfc) el.requestVideoFrameCallback(bild);
      else requestAnimationFrame(bild);
    };
    const bild = (jetzt, meta) => {
      if (laufRef.current !== lauf || lauf.beendet) return;
      /* Ohne Bildrückruf: dasselbe Videobild nicht zweimal messen. */
      if (el.readyState < 2 || (!rvfc && el.currentTime === lauf.letzteZeit)) { weiter(); return; }
      lauf.letzteZeit = el.currentTime;
      /* Aufnahmezeit des Bilds (ms): Daraus weiß die Abwicklung, wie
         weit sich die Flasche höchstens gedreht haben kann, wenn das
         Telefon Bilder auslässt. */
      const zeit = meta && Number.isFinite(meta.mediaTime) ? meta.mediaTime * 1000 : performance.now();
      try {
        verarbeiten(lauf, zeit);
      } catch (e) {
        lauf.beendet = true;
        laufRef.current = null;
        kameraStoppen();
        setLaufFehler(t("Die Aufnahme ist fehlgeschlagen."));
        setPhase("fehler");
        return;
      }
      weiter();
    };
    weiter();
  };

  const umrissZeichnen = (lauf, punkte) => {
    const pfad = umrissRef.current;
    if (!pfad) return;
    pfad.setAttribute("d", punkte && punkte.length
      ? "M" + punkte.map((p) => lauf.quellen.zuVideo(p).map((v) => v.toFixed(1)).join(",")).join(" L") + " Z"
      : "");
  };

  const verarbeiten = (lauf, zeit) => {
    const bild = lauf.quellen.uebersicht();
    if (lauf.modus === "vorschau") {
      const r = vorschauSchritt(lauf.z, bild, lauf.vorgabe);
      umrissZeichnen(lauf, r.umriss);
      if (r.bereit !== lauf.bereit) { lauf.bereit = r.bereit; setErkannt(r.bereit); }
      return;
    }
    const st = abwicklungSchritt(lauf.z, bild, lauf.quellen.voll, zeit);
    umrissZeichnen(lauf, st.verloren ? null : umrissLinie(lauf.z.lage, lauf.z.profil));
    /* Nur melden, was sich sichtbar ändert. */
    const kennung = [Math.round(st.fortschritt * 100), st.zuSchnell, st.steht, st.verloren].join();
    if (kennung !== lauf.gemeldet) { lauf.gemeldet = kennung; setStand(st); }
    if (st.fertig) auswerten(lauf);
  };

  /* Vorschau: neu, sobald Kamera, Bildgröße oder Form feststehen. */
  useEffect(() => {
    if (phase !== "kamera" || quelle !== "video" || !video || !rahmen || !videoRef.current) return undefined;
    const quellen = bildquellen(videoRef.current, video.b, video.h);
    const z = abwicklungBeginnen(form);
    const f = quellen.faktor;
    const vorgabe = {
      cx: (rahmen.cx - quellen.quelle.x) * f,
      oben: (rahmen.oben - quellen.quelle.y) * f,
      S: rahmen.hoehe * f / z.vorlage.uBoden,
    };
    const lauf = { modus: "vorschau", z, quellen, vorgabe, bereit: false, letzteZeit: -1 };
    laufRef.current = lauf;
    setErkannt(false);
    umrissZeichnen(lauf, null);
    schleife(lauf);
    /* Beim Start geht derselbe Lauf in die Aufnahme über (modus) und
       darf hier nicht beendet werden. */
    return () => { if (laufRef.current === lauf && lauf.modus === "vorschau") laufRef.current = null; };
  }, [phase, quelle, video && video.b, video && video.h, form]);

  /* ── Aufnahme ── */
  const starten = () => {
    const lauf = laufRef.current;
    if (!lauf || lauf.modus !== "vorschau" || !lauf.bereit) return;
    aufnahmeStarten(lauf.z, { faktor: lauf.quellen.faktor });
    lauf.modus = "aufnahme";
    lauf.gemeldet = null;
    setStand(null); setLaufFehler(null);
    setPhase("laeuft");
  };

  const auswerten = async (lauf) => {
    if (!lauf || lauf.beendet) return;
    lauf.beendet = true;
    kameraStoppen();
    setRechnet(0);
    setPhase("rechnet");
    let erg;
    try {
      erg = await abwicklungAuswerten(lauf.z, { beiFortschritt: (a) => { if (laufRef.current === lauf) setRechnet(a); } });
    } catch (e) {
      erg = { fehler: "intern" };
    }
    if (laufRef.current !== lauf) return;      /* inzwischen abgebrochen */
    laufRef.current = null;
    if (erg.fehler) {
      setLaufFehler(erg.fehler === "keinUmlauf" || erg.fehler === "zuKurz"
        ? t("Es wurde keine volle Umdrehung erkannt. Bitte die Flasche ganz herumdrehen, bis der Balken voll ist, und dabei im Umriss halten.")
        : t("Die Aufnahme ist fehlgeschlagen."));
      setPhase("fehler");
      return;
    }
    ergebnisZeigen(erg);
  };

  /* ── Fotos ── */
  const fotosDazu = (e) => {
    const dateien = [...(e.target.files || [])].filter((d) => !d.type || d.type.startsWith("image/"));
    e.target.value = "";
    if (!dateien.length) return;
    setLaufFehler(null);
    setFotos((alt) => [...alt, ...dateien.map((datei) => ({
      id: Math.random().toString(36).slice(2), datei, url: URL.createObjectURL(datei),
    }))].slice(0, FOTOS_MAX));
  };
  const fotoWeg = (id) => setFotos((alt) => {
    const weg = alt.find((f) => f.id === id);
    if (weg) URL.revokeObjectURL(weg.url);
    return alt.filter((f) => f.id !== id);
  });

  const fotosAuswerten = async () => {
    if (fotos.length < 3 || laufRef.current) return;
    const lauf = { modus: "fotos", beendet: false };
    laufRef.current = lauf;
    setRechnet(0); setLaufFehler(null);
    setPhase("rechnet");
    let erg;
    try {
      const bilder = [];
      for (let i = 0; i < fotos.length; i++) {
        bilder.push(await fotoLesen(fotos[i].datei));
        if (laufRef.current !== lauf) return;
        setRechnet(0.15 * (i + 1) / fotos.length);
      }
      erg = await fotoserieAuswerten(bilder, form, { beiFortschritt: (a) => { if (laufRef.current === lauf) setRechnet(0.15 + 0.85 * a); } });
    } catch (e) {
      erg = { fehler: "intern" };
    }
    if (laufRef.current !== lauf) return;
    laufRef.current = null;
    if (erg.fehler) {
      setLaufFehler(fotoFehlertext(erg));
      setPhase("fehler");
      return;
    }
    ergebnisZeigen(erg);
  };

  const ergebnisZeigen = (erg) => {
    const leinwand = document.createElement("canvas");
    leinwand.width = erg.breite; leinwand.height = erg.hoehe;
    leinwand.getContext("2d").putImageData(new ImageData(erg.daten, erg.breite, erg.hoehe), 0, 0);
    const gefunden = kapselErkennen(erg, erg.form);
    const art = jg.rundum && jg.rundum.kapsel ? jg.rundum.kapsel.art : kapselArt(jg);
    setKapsel({ art, anteil: Math.round(gefunden.anteil * 1000) / 1000, farbe: gefunden.farbe });
    setErgebnis({
      leinwand,
      flachUrl: leinwand.toDataURL("image/jpeg", 0.7),
      seitenverhaeltnis: erg.breite / erg.hoehe,
      form: erg.form,
      vorne: Math.round(erg.vorne * 1000) / 1000,
      info: {
        kapselSicher: gefunden.sicher,
        luecken: erg.abdeckung < 0.97,
        ausFotos: erg.fotos > 0,
        /* Ohne gemessenen Umriss folgen Hals und Schulter der
           gewählten Grundform — bei einer anders geformten Flasche
           sitzt das Bild dann nicht ganz. */
        ohneUmriss: !erg.form.gemessen,
        /* aus Fotos: andere Grundform als gewählt erkannt */
        formErkannt: erg.formSchluessel && erg.formSchluessel !== form && FLASCHENFORMEN[erg.formSchluessel] ? erg.formSchluessel : null,
      },
    });
    if (erg.formSchluessel && erg.formSchluessel !== form && FLASCHENFORMEN[erg.formSchluessel]) setForm(erg.formSchluessel);
    setPhase("pruefen");
  };

  const abbrechen = () => {
    laufRef.current = null;
    kameraStoppen();
    onCancel();
  };

  const wiederholen = () => {
    laufRef.current = null;
    setErgebnis(null); setKapsel(null); setStand(null); setLaufFehler(null); setSpeicherFehler(null); setLupe(false);
    setPhase("kamera");
  };

  const neuBeginnen = () => {
    /* Kamera bleibt an, die Vorschau beginnt von vorn */
    laufRef.current = null;
    setStand(null);
    setPhase("kamera");
  };

  const speichern = async () => {
    if (!ergebnis || speichert) return;
    setSpeichert(true); setSpeicherFehler(null);
    try {
      const foto = await rundumHochladen(jg.id, ergebnis.leinwand);
      onSave({
        ...foto, form, kapsel: { ...kapsel }, vorne: ergebnis.vorne,
        ...(ergebnis.form.gemessen ? { profil: { hoehe: ergebnis.form.hoehe, kapsel: ergebnis.form.kapsel, profil: ergebnis.form.profil } } : {}),
      });
    } catch (e) {
      setSpeicherFehler((e && e.message) || t("Der Upload ist fehlgeschlagen."));
      setSpeichert(false);
    }
  };

  /* ── Darstellung ── */
  const formDaten = formVon(form);
  const hinweis = (() => {
    if (phase === "kamera" && quelle === "fotos") {
      if (!fotos.length) return t("Fotos aufnehmen oder auswählen — am besten sechs rundum.");
      if (fotos.length < 3) return t.plural(fotos.length, "Ein Foto. Mindestens drei, besser sechs.", "{n} Fotos. Mindestens drei, besser sechs.");
      return t("{n} Fotos. Berechnen tippen.", { n: fotos.length });
    }
    if (phase === "kamera") {
      return erkannt
        ? t("Flasche erkannt. Start tippen und die Flasche langsam einmal ganz herumdrehen — umgreifen ist erlaubt.")
        : t("Flasche in den Umriss stellen. Telefon ruhig halten, am besten aufgestützt.");
    }
    if (phase === "rechnet") return t("Wird berechnet …");
    if (!stand) return t("Langsam drehen, bis der Balken voll ist");
    if (stand.verloren) return t("Flasche nicht erkannt — bitte im Umriss halten");
    if (stand.zuSchnell) return t("Etwas langsamer drehen");
    if (stand.steht) return t("Flasche weiterdrehen");
    return t("Langsam drehen, bis der Balken voll ist");
  })();

  if (phase === "pruefen" && ergebnis) {
    return (
      <div className="wl-rundumaufnahme ist-pruefen" role="dialog" aria-modal="true" aria-label={t("Rundum-Etikett prüfen")}>
        <div className="wl-rundumkopf">
          <span className="wl-rundumtitel">{t("Rundum-Etikett prüfen")}</span>
          <button className="wl-rundumzu" onClick={abbrechen} aria-label={t("Schließen")}>×</button>
        </div>
        <div className="wl-rundumpruefen">
          <FlaschenModell form={ergebnis.form} kapsel={kapsel} bild={ergebnis.leinwand} vorne={ergebnis.vorne}
            alt={t("Vorschau des Rundum-Etiketts")} onTippen={() => setLupe(true)} />
          {lupe && (
            <FlaschenLupe form={ergebnis.form} kapsel={kapsel} bild={ergebnis.leinwand} vorne={ergebnis.vorne}
              alt={t("Vorschau des Rundum-Etiketts")} onSchliessen={() => setLupe(false)} />
          )}
          <div className="wl-rundumregler">
            {/* Oberer Teil der Abwicklung in ihrem Seitenverhältnis —
                dort liegt jede mögliche Kapselkante */}
            <div className="wl-rundumflachvorschau" aria-hidden="true"
              style={{ aspectRatio: `${ergebnis.seitenverhaeltnis} / 0.47` }}>
              <div className="wl-rundumflachbild">
                <img src={ergebnis.flachUrl} alt="" />
                {kapsel.art !== "ohne" && <span className="wl-rundumkapsellinie" style={{ top: `${kapsel.anteil * 100}%` }} />}
              </div>
            </div>
            <fieldset className="wl-rundumwahl">
              <legend className="wl-feldname">{t("Kapsel")}</legend>
              {KAPSEL_ARTEN.map((a) => (
                <button key={a} type="button" className={"wl-appchip" + (kapsel.art === a ? " ist-aktiv" : "")}
                  aria-pressed={kapsel.art === a} onClick={() => setKapsel((k) => ({ ...k, art: a }))}>
                  {t(KAPSEL_LABEL[a])}
                </button>
              ))}
            </fieldset>
            {kapsel.art !== "ohne" && (
              <label className="wl-feld">
                <span className="wl-feldname">{t("Kapsellänge")}</span>
                <input type="range" min="0.03" max="0.45" step="0.005" value={kapsel.anteil}
                  onChange={(e) => setKapsel((k) => ({ ...k, anteil: Number(e.target.value) }))} />
              </label>
            )}
            {!ergebnis.info.kapselSicher && kapsel.art !== "ohne" && (
              <p className="wl-hinweis">{t("Die Kapselkante war nicht eindeutig zu erkennen. Bitte mit dem Regler an die Linie anpassen.")}</p>
            )}
            {ergebnis.info.formErkannt && (
              <p className="wl-hinweis">{t("Die Flasche hat die Form {form}. Die Form wurde angepasst.", { form: FLASCHENFORMEN[ergebnis.info.formErkannt].name })}</p>
            )}
            {ergebnis.info.ohneUmriss && (
              <p className="wl-hinweis">{t("Der Umriss der Flasche war nicht sicher zu erkennen. Hals und Kapsel folgen der gewählten Form und können verzerrt sein.")}</p>
            )}
            {ergebnis.info.luecken && (
              <p className="wl-hinweis">{ergebnis.info.ausFotos
                ? t("Einzelne Stellen sind nur von der Seite zu sehen und unscharf. Mehr Fotos rundum helfen.")
                : t("Einzelne Stellen wurden ergänzt. Langsameres Drehen ergibt ein schärferes Bild.")}</p>
            )}
            {speicherFehler && <p className="wl-fehler">{speicherFehler}</p>}
          </div>
        </div>
        <div className="wl-rundumfuss">
          <Btn variante="ghost" onClick={wiederholen} disabled={speichert}>{t("Wiederholen")}</Btn>
          <Btn variante="voll" onClick={speichern} disabled={speichert}>{speichert ? t("Wird hochgeladen …") : t("Speichern")}</Btn>
        </div>
      </div>
    );
  }

  const balken = phase === "rechnet" ? rechnet : (stand && stand.fortschritt) || 0;
  return (
    <div className="wl-rundumaufnahme" role="dialog" aria-modal="true" aria-label={t("Rundum-Etikett aufnehmen")}>
      {quelle === "video" && <div className="wl-rundumbild">
        <video ref={videoRef} className="wl-rundumvideo" playsInline muted autoPlay
          onLoadedMetadata={videoBereit} onResize={videoBereit} />
        {rahmen && (phase === "kamera" || phase === "laeuft") && (
          <svg className="wl-rundumumriss" viewBox={`0 0 ${video.b} ${video.h}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            {phase === "kamera" && (
              <path d={umrissPfad(formDaten, rahmen.cx, rahmen.oben, rahmen.hoehe)} className={"wl-rundumkontur" + (erkannt ? " ist-blass" : "")} />
            )}
            <path ref={umrissRef} d="" className="wl-rundumkontur ist-erkannt" />
          </svg>
        )}
        {phase === "rechnet" && <div className="wl-rundumrechnet" />}
      </div>}

      <div className="wl-rundumkopf">
        <span className="wl-rundumtitel">{t("Rundum-Etikett")}</span>
        <button className="wl-rundumzu" onClick={abbrechen} aria-label={t("Schließen")}>×</button>
      </div>

      {quelle === "fotos" && (
        <div className={"wl-rundumfotos" + (phase === "rechnet" ? " ist-blass" : "")}>
          <ol className="wl-rundumanleitung">
            <li>{t("Flasche auf eine Fläche stellen. Gleichmäßiges Licht, kein Blitz.")}</li>
            <li>{t("Telefon etwa auf Höhe der Flaschenmitte, die ganze Flasche im Bild.")}</li>
            <li>{t("Nach jedem Foto die Flasche um etwa 60° weiterdrehen, immer in dieselbe Richtung, am Bauch angefasst (eine lose Kapsel dreht sonst mit) — sechs Fotos rundum.")}</li>
            <li>{t("Foto 1 ist die Vorderseite: mit dem Vorderetikett in der Mitte beginnen. Die Fotos lassen sich durch Ziehen umsortieren.")}</li>
          </ol>
          {fotos.length > 0 && (
            <FotoListe fotos={fotos} aktiv={phase !== "rechnet"} onReihe={setFotos} onWeg={fotoWeg} />
          )}
          {phase !== "rechnet" && fotos.length < FOTOS_MAX && (
            <div className="wl-rundumfotoknoepfe">
              <Btn variante="ghost" onClick={() => aufnahmeRef.current && aufnahmeRef.current.click()}>{t("Foto aufnehmen")}</Btn>
              <Btn variante="ghost" onClick={() => auswahlRef.current && auswahlRef.current.click()}>{t("Fotos auswählen")}</Btn>
            </div>
          )}
          <input ref={aufnahmeRef} type="file" accept="image/*" capture="environment" hidden onChange={fotosDazu} />
          <input ref={auswahlRef} type="file" accept="image/*" multiple hidden onChange={fotosDazu} />
        </div>
      )}

      <div className="wl-rundumfuss ist-unten">
        {(phase === "laeuft" || phase === "rechnet") && (
          <div className="wl-rundumfortschritt" role="progressbar" aria-valuemin={0} aria-valuemax={100}
            aria-valuenow={Math.round(balken * 100)}>
            <span style={{ width: `${Math.round(balken * 100)}%` }} />
          </div>
        )}
        {kameraFehler && quelle === "video" && <p className="wl-rundumtext ist-fehler">{kameraFehler}</p>}
        {phase === "fehler" && laufFehler && <p className="wl-rundumtext ist-fehler">{laufFehler}</p>}
        {(!kameraFehler || quelle === "fotos") && phase !== "fehler" && <p className="wl-rundumtext" aria-live="polite">{hinweis}</p>}

        {phase === "kamera" && (
          <div className="wl-rundumformen" role="group" aria-label={t("Aufnahmeart")}>
            {[["video", t("Video")], ["fotos", t("Fotos")]].map(([q, name]) => (
              <button key={q} type="button" className={"wl-appchip" + (quelle === q ? " ist-aktiv" : "")}
                aria-pressed={quelle === q} onClick={() => setQuelle(q)}>{name}</button>
            ))}
          </div>
        )}
        {phase === "kamera" && (quelle === "fotos" || !kameraFehler) && (
          <div className="wl-rundumformen" role="group" aria-label={t("Flaschenform")}>
            {FORM_REIHE.map((f) => (
              <button key={f} type="button" className={"wl-appchip" + (form === f ? " ist-aktiv" : "")}
                aria-pressed={form === f} onClick={() => setForm(f)}>{FLASCHENFORMEN[f].name}</button>
            ))}
          </div>
        )}

        <div className="wl-rundumknoepfe">
          <Btn variante="ghost" onClick={abbrechen}>{t("Abbrechen")}</Btn>
          {phase === "kamera" && quelle === "video" && !kameraFehler && (
            <Btn variante="voll" autoFocus onClick={starten} disabled={!erkannt}>{t("Start")}</Btn>
          )}
          {phase === "kamera" && quelle === "video" && kameraFehler && <Btn variante="voll" onClick={kameraStarten}>{t("Erneut versuchen")}</Btn>}
          {phase === "kamera" && quelle === "fotos" && (
            <Btn variante="voll" onClick={fotosAuswerten} disabled={fotos.length < 3}>{t("Berechnen")}</Btn>
          )}
          {/* „Fertig“ ab 90 % (gut 1,08 Umdrehungen der groben Schätzung):
              Misst sie genau, reicht das bei einem gemessenen Umfang bis
              rund 650 Spalten noch für die Überlappung der Auswertung */}
          {phase === "laeuft" && (stand && stand.fortschritt >= 0.9
            ? <Btn variante="voll" onClick={() => auswerten(laufRef.current)}>{t("Fertig")}</Btn>
            : <Btn variante="voll" onClick={neuBeginnen}>{t("Neu beginnen")}</Btn>)}
          {phase === "fehler" && <Btn variante="voll" onClick={wiederholen}>{t("Wiederholen")}</Btn>}
        </div>
      </div>
    </div>
  );
}


export { RundumAufnahme, umrissPfad };
